import { useMemo } from "react";
import { ApiRequestError } from "@src/packages/api/api-error";
import { useOrganization } from "./useOrganization";
import { useOrganizationApi, useOrganizationsApi } from "./useOrganizationsApi";
import { useOrganizations } from "./useOrganizations";
import {
  MOCK_PHARMACY_DIRECTORY,
  toPharmacyDirectoryRecord,
  type PharmacyDirectoryRecord,
  type PharmacyDirectorySource,
} from "../pharmacyDirectory";

export function usePharmacyDirectory(options?: { demo?: boolean }) {
  const demo = options?.demo === true;
  const api = useOrganizationsApi(
    { role: "pharmacy", countryCode: "US", limit: 50 },
    { enabled: !demo },
  );
  const curated = useOrganizations({ type: ["pharmacy"] }, "name");

  // Curated is the source of truth for the pharmacy directory — it carries
  // logos, addresses, descriptions, and location. The API is a fallback only
  // when the curated dataset has not loaded yet.
  const source: PharmacyDirectorySource = demo
    ? "demo"
    : curated.data.length
      ? "curated"
      : api.data?.data.length
        ? "live"
        : "curated";
  const data = useMemo<PharmacyDirectoryRecord[]>(() => {
    if (demo) return MOCK_PHARMACY_DIRECTORY;
    const organizations = curated.data.length
      ? curated.data
      : (api.data?.data ?? []);
    return organizations.map((organization) =>
      toPharmacyDirectoryRecord(organization, source),
    );
  }, [api.data?.data, curated.data, demo, source]);

  return {
    data,
    source,
    isLoading:
      !demo && data.length === 0 && (api.isLoading || curated.isLoading),
    error: demo || data.length > 0 ? null : (api.error ?? curated.error),
    isFetching: api.isFetching || curated.isRetrying,
    // Retry both sources: the bundled dataset load can fail on its own.
    refetch: () => {
      if (!api.isPending) void api.refetch();
      if (curated.error) curated.retry();
    },
  };
}

export function usePharmacy(pharmacyId: string, options?: { demo?: boolean }) {
  const demo = options?.demo === true;
  const api = useOrganizationApi(!demo ? pharmacyId : null);
  const curated = useOrganization(pharmacyId);
  const demoPharmacy = demo
    ? MOCK_PHARMACY_DIRECTORY.find((pharmacy) => pharmacy.id === pharmacyId)
    : undefined;
  // Prefer curated over API for the same reason as the list hook above.
  const source: PharmacyDirectorySource = demo
    ? "demo"
    : curated.data
      ? "curated"
      : api.data
        ? "live"
        : "curated";
  const organization = curated.data ?? api.data;
  const data =
    demoPharmacy ??
    (organization ? toPharmacyDirectoryRecord(organization, source) : null);
  // A pharmacy the directory doesn't have is not a failed load.
  const apiLoadError =
    api.error instanceof ApiRequestError && api.error.status === 404
      ? null
      : api.error;

  return {
    data,
    source,
    isLoading: !data && !demo && (api.isLoading || curated.isLoading),
    error: data || demo ? null : (api.error ?? curated.error),
    /** Only a failed load, never a pharmacy that isn't in the directory. */
    loadError: data || demo ? null : (curated.loadError ?? apiLoadError),
    isFetching: api.isFetching || curated.isRetrying,
    refetch: () => {
      if (!api.isPending) void api.refetch();
      if (curated.loadError) curated.retry();
    },
  };
}
